import React, { useCallback } from "react";
import {
  ThemeProvider,
  Stepper,
  Step,
  StepButton,
  Grid,
  FormControl,
  FormLabel,
  createTheme,
} from "@mui/material";
import {
  EShortageInvestigationStatus,
  FREIGHT_LOCATED_STEP_MAP,
  STEP_DAY_MAPPING,
  STEP_STATUS_MAP,
  THEME,
} from "../../../constants";

interface InvestigationStepperProps {
  activeStep: number;
  steps: string[];
  isFreightLocated?: boolean;
  lastStatusNumber?: number;
  status?: number;
  setIsCompleting: (isCompleting: boolean) => void;
  setFormFieldsDisabled: (disabled: boolean) => void;
  setIsEditMode: (isEditMode: boolean) => void;
  reset: () => void;
  setActiveStep: (step: number) => void;
}

export const InvestigationStepper: React.FC<InvestigationStepperProps> = ({
  activeStep,
  steps,
  isFreightLocated = false,
  lastStatusNumber,
  status,
  setIsCompleting,
  setFormFieldsDisabled,
  setIsEditMode,
  reset,
  setActiveStep,
}) => {
  const handleStep = (step: number) => () => {
    setIsCompleting(false);
    const statusStep = isFreightLocated
      ? FREIGHT_LOCATED_STEP_MAP[
          lastStatusNumber as keyof typeof FREIGHT_LOCATED_STEP_MAP
        ]
      : STEP_STATUS_MAP[status as keyof typeof STEP_STATUS_MAP];
    if (statusStep < step) return;
    setFormFieldsDisabled(true);
    setIsEditMode(true);
    reset();
    setActiveStep(step);
  };

  const stepClass = useCallback(
    (index: number) => {
      const stapStatus = isFreightLocated
        ? FREIGHT_LOCATED_STEP_MAP[
            lastStatusNumber as keyof typeof FREIGHT_LOCATED_STEP_MAP
          ]
        : STEP_STATUS_MAP[status as keyof typeof STEP_STATUS_MAP];
      if (isFreightLocated && stapStatus >= index) {
        return "completeStep";
      }
      if (
        (index === activeStep && stapStatus > activeStep) ||
        stapStatus > index ||
        (!isFreightLocated &&
          (status === EShortageInvestigationStatus.pendingSrApproval ||
            status === EShortageInvestigationStatus.complete))
      ) {
        return "completeStep";
      } else if (index === activeStep || stapStatus === index) {
        return "activeStep";
      }
      return "";
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isFreightLocated, lastStatusNumber, status, activeStep, STEP_STATUS_MAP]
  );

  const theme = createTheme(THEME);
  const activeDay =
    STEP_DAY_MAPPING &&
    typeof STEP_DAY_MAPPING === "object" &&
    STEP_DAY_MAPPING.hasOwnProperty(activeStep)
      ? STEP_DAY_MAPPING[activeStep as keyof typeof STEP_DAY_MAPPING]
      : "";
  return (
    <>
      <Grid item md={12} xs={12}>
        <FormControl className="custom-group">
          <FormLabel>
            Investigation Day&nbsp;
            {activeDay}
            <sup>*</sup>{" "}
          </FormLabel>
        </FormControl>
      </Grid>
      <ThemeProvider theme={theme}>
        <Stepper data-testid="ds-stepper" nonLinear activeStep={activeStep}>
          {steps.map((label, index) => (
            <Step key={label} className={stepClass(index)}>
              <StepButton
                data-testid={`ds-stepper-step-${index}`}
                onClick={index === activeStep ? void 0 : handleStep(index)}
                disableRipple
              >
                {label}
              </StepButton>
            </Step>
          ))}
        </Stepper>
      </ThemeProvider>
    </>
  );
};
